import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  ArrowLeft, Play, Pause, Download, Share2, Search, MapPin, AlertTriangle, Check,
} from 'lucide-react';
import { apiClient } from '@/lib/api';
import { Button } from '@/components/ui/Button';

/*
 * PR-TRIP-AUDIT Step 7 — Vehicle History.
 * Pick a truck and a day (or a range), see the route it really drove, every
 * stop of 45 minutes or more, and every leg between two real sites. A leg with
 * no row in the operations records is marked UNREPORTED in red.
 *
 * Plates only. No internal vehicle codes on screen.
 */

interface Pt { t: string; lat: number; lng: number; speed: number | null }
interface Stop {
  started_at: string; ended_at: string; minutes: number;
  lat: number; lng: number; terminal: string | null; role: string | null;
}
type LegState = 'in_records' | 'unreported' | 'not_billable';
interface Leg {
  plate: string; from_terminal: string; to_terminal: string;
  departed_at: string; arrived_at: string; km: number; matched: boolean;
  rec_vendor: string | null; rec_item: string | null; rec_rate: string | null;
  /* in_records = billed and found · unreported = billed lane, no row ·
     not_billable = the records never bill that lane, so no row is due. */
  state: LegState; queue_id: string | null; queue_status: string | null;
}
interface Track {
  plate: string; from: string; to: string; points: Pt[]; totalPoints: number;
  stops: Stop[]; legs: Leg[];
  summary: {
    legs: number; inRecords: number; unreported: number; notBillable: number;
    km: number; daysWithGps: number;
  };
}
interface Vehicle {
  vehicle_id: string; plate: string; code: string; vehicle_type: string;
  project_id: string | null; fixes: number; last_seen: string | null;
}

const iso = (d: Date) => d.toISOString().slice(0, 10);
const shiftDays = (n: number) => iso(new Date(Date.now() - n * 86400000));
const hhmm = (s: string) => s.slice(11, 16);
const dayOf = (s: string) => s.slice(0, 10);
const niceDay = (s: string) =>
  new Date(`${dayOf(s)}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
const mins = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 60000);
const asDur = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m` : `${m}m`);

/** Green for a loading site, red for unloading, grey when the zone has no role. */
const roleColour = (role: string | null) =>
  role === 'loading' ? '#10b981' : role === 'unloading' ? '#ef4444' : role === 'both' ? '#f59e0b' : '#94a3b8';

async function download(url: string, params: Record<string, string>, fallback: string) {
  const res = await apiClient.get(url, { params, responseType: 'blob' });
  const cd = String(res.headers['content-disposition'] ?? '');
  const name = /filename="([^"]+)"/.exec(cd)?.[1] ?? fallback;
  const href = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = href; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 4000);
}

export default function VehicleHistoryPage() {
  const [scope, setScope] = useState('all');
  const [plate, setPlate] = useState<string>('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState(shiftDays(7));
  const [to, setTo] = useState(shiftDays(0));
  const [cursor, setCursor] = useState(0);      // index into points
  const [playing, setPlaying] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [shared, setShared] = useState<string | null>(null);

  const { data: vlist } = useQuery({
    queryKey: ['vh-vehicles', scope],
    queryFn: async () => (await apiClient.get<{ vehicles: Vehicle[]; untracked: Array<{ plate: string }> }>(
      '/api/v1/vehicle-history/vehicles',
      { params: scope === 'all' ? {} : { projectScope: scope } },
    )).data,
  });

  const { data: status } = useQuery({
    queryKey: ['vh-status'],
    queryFn: async () => (await apiClient.get<{
      lastRun: { finished_at: string; unreported: number } | null;
      legCoverage: { lo: string | null; hi: string | null } | null;
    }>('/api/v1/vehicle-history/status')).data,
  });

  // First load: land on the first truck that has GPS so the page is never blank.
  useEffect(() => {
    if (!plate && vlist?.vehicles?.length) setPlate(vlist.vehicles[0].plate);
  }, [vlist, plate]);

  const { data: track, isLoading, isError, error } = useQuery({
    queryKey: ['vh-track', plate, from, to],
    queryFn: async () => (await apiClient.get<Track>('/api/v1/vehicle-history/track',
      { params: { plate, from, to } })).data,
    enabled: !!plate,
  });

  // Plate digits are what the yard actually says out loud, so search on those.
  const vehicles = useMemo(() => {
    const all = vlist?.vehicles ?? [];
    const digits = search.replace(/\D/g, '');
    if (!search.trim()) return all;
    return all.filter((v) =>
      (digits && v.plate.replace(/\D/g, '').includes(digits))
      || v.plate.toLowerCase().includes(search.trim().toLowerCase()));
  }, [vlist, search]);

  /* ----------------------------------------------------------------- map */
  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layer = useRef<L.LayerGroup | null>(null);
  const truck = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    const map = L.map(mapEl.current, { zoomControl: true }).setView([23.9, 45.1], 5.4);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      { maxZoom: 18, attribution: '© OpenStreetMap' }).addTo(map);
    layer.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; layer.current = null; truck.current = null; };
  }, []);

  // Redraw route + pins whenever the track changes.
  useEffect(() => {
    const map = mapRef.current; const lg = layer.current;
    if (!map || !lg) return;
    lg.clearLayers(); truck.current = null;
    setCursor(0); setPlaying(false);
    if (!track?.points.length) return;

    const line = track.points.map((p) => [p.lat, p.lng] as [number, number]);
    L.polyline(line, { color: '#b8860b', weight: 3, opacity: 0.85 }).addTo(lg);

    track.stops.forEach((s, i) => {
      const colour = roleColour(s.role);
      L.marker([s.lat, s.lng], {
        icon: L.divIcon({
          className: '',
          html: `<div style="width:26px;height:26px;border-radius:50%;background:${colour};`
            + 'color:#fff;font:600 11px/26px system-ui;text-align:center;'
            + `box-shadow:0 1px 4px rgba(0,0,0,.4);border:2px solid #fff">${i + 1}</div>`,
          iconSize: [26, 26], iconAnchor: [13, 13],
        }),
      }).addTo(lg).bindPopup(
        `<b>${i + 1}. ${s.terminal ?? 'Unnamed stop'}</b><br>${s.role ?? 'role not set'}<br>`
        + `${niceDay(s.started_at)} ${hhmm(s.started_at)} → ${hhmm(s.ended_at)}<br>`
        + `Stood for ${asDur(s.minutes)}`,
      );
    });

    truck.current = L.marker(line[0], {
      icon: L.divIcon({
        className: '',
        html: '<div style="width:16px;height:16px;border-radius:4px;background:#111;'
          + 'border:2px solid #fff;box-shadow:0 0 0 3px rgba(17,17,17,.25)"></div>',
        iconSize: [16, 16], iconAnchor: [8, 8],
      }),
    }).addTo(lg);

    map.fitBounds(L.latLngBounds(line).pad(0.12));
  }, [track]);

  // Playback. The marker moves along the sampled polyline.
  useEffect(() => {
    const line = track?.points;
    if (!playing || !line?.length) return;
    const id = window.setInterval(() => {
      setCursor((c) => {
        if (c >= line.length - 1) { setPlaying(false); return c; }
        return c + 1;
      });
    }, 60);
    return () => window.clearInterval(id);
  }, [playing, track]);

  useEffect(() => {
    const p = track?.points?.[cursor];
    if (p && truck.current) truck.current.setLatLng([p.lat, p.lng]);
  }, [cursor, track]);

  const at = track?.points?.[cursor];
  const s = track?.summary;
  const reportParams = { from, to };

  const makeShare = async (kind: 'vehicle' | 'fleet') => {
    setBusy('share');
    try {
      const res = await apiClient.post<{ token: string; path: string }>(
        '/api/v1/vehicle-history/share',
        kind === 'vehicle' ? { kind, plate, from, to } : { kind, from, to },
      );
      const url = `${window.location.origin}${res.data.path}`;
      setShared(url);
      try { await navigator.clipboard.writeText(url); } catch { /* clipboard blocked, link still shown */ }
    } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2 justify-between">
          <div className="font-display text-lg">Vehicle history</div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={scope} onChange={(e) => setScope(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm">
              <option value="all">All projects</option>
              <option value="neom">NEOM</option>
              <option value="dumpers">Dumpers</option>
              <option value="riyadh">Flatbed</option>
            </select>
            <Button variant="ghost" size="sm" className="gap-2"
              onClick={() => { window.location.href = '/dashboard/unreported-trips'; }}>
              <AlertTriangle className="h-4 w-4" />
              Unreported
              {status?.lastRun?.unreported ? (
                <span className="ml-1 rounded bg-rose-500/15 text-rose-600 px-1.5 text-xs">
                  {status.lastRun.unreported}
                </span>
              ) : null}
            </Button>
            <Button variant="ghost" size="sm" className="gap-2"
              onClick={() => { window.location.href = '/dashboard'; }}>
              <ArrowLeft className="h-4 w-4" /> Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-[1400px] mx-auto w-full px-4 sm:px-6 py-5">
        {/* ---- controls -------------------------------------------------- */}
        <div className="grid gap-3 lg:grid-cols-[320px_1fr] mb-4">
          <div className="rounded-xl border border-border bg-card p-3 min-w-0">
            <div className="relative mb-2">
              <Search className="h-4 w-4 absolute start-2 top-2.5 text-muted-foreground" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search plate digits, e.g. 6186"
                inputMode="numeric"
                className="h-9 w-full rounded-md border border-input bg-background ps-8 pe-2 text-sm" />
            </div>
            <div className="max-h-56 overflow-y-auto -mx-1 px-1">
              {vehicles.map((v) => (
                <button key={v.vehicle_id} onClick={() => setPlate(v.plate)}
                  className={`w-full text-start px-2 py-1.5 rounded-md text-sm flex items-center justify-between
                    ${v.plate === plate ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted/60'}`}>
                  <span>{v.plate}</span>
                  <span className="text-xs text-muted-foreground">{v.vehicle_type}</span>
                </button>
              ))}
              {!vehicles.length && (
                <div className="text-sm text-muted-foreground px-2 py-3">No truck matches that.</div>
              )}
            </div>
            {!!vlist?.untracked?.length && (
              <div className="mt-2 pt-2 border-t border-border text-xs text-muted-foreground">
                {vlist.untracked.length} active trucks have no tracker, so they have no history here.
              </div>
            )}
          </div>

          <div className="rounded-xl border border-border bg-card p-3 min-w-0">
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-sm">
                <span className="block text-xs text-muted-foreground mb-1">From</span>
                <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-muted-foreground mb-1">To</span>
                <input type="date" value={to} min={from} max={iso(new Date())}
                  onChange={(e) => setTo(e.target.value)}
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm" />
              </label>
              <div className="flex flex-wrap gap-1.5">
                {([['Yesterday', 1], ['Last 7', 7], ['Last 30', 30], ['Last 90', 90]] as const).map(([lbl, n]) => (
                  <Button key={lbl} variant="outline" size="sm"
                    onClick={() => { setFrom(shiftDays(n)); setTo(shiftDays(n === 1 ? 1 : 0)); }}>
                    {lbl}
                  </Button>
                ))}
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" className="gap-2" disabled={!plate || busy === 'x1'}
                onClick={async () => {
                  setBusy('x1');
                  try {
                    await download('/api/v1/vehicle-history/report',
                      { kind: 'vehicle', format: 'xlsx', plate, ...reportParams }, 'movement.xlsx');
                  } finally { setBusy(null); }
                }}>
                <Download className="h-4 w-4" /> Movement Excel
              </Button>
              <Button variant="outline" size="sm" className="gap-2" disabled={!plate || busy === 'p1'}
                onClick={async () => {
                  setBusy('p1');
                  try {
                    await download('/api/v1/vehicle-history/report',
                      { kind: 'vehicle', format: 'pdf', plate, ...reportParams }, 'movement.pdf');
                  } finally { setBusy(null); }
                }}>
                <Download className="h-4 w-4" /> Movement PDF
              </Button>
              <Button variant="outline" size="sm" className="gap-2" disabled={busy === 'x2'}
                onClick={async () => {
                  setBusy('x2');
                  try {
                    await download('/api/v1/vehicle-history/report',
                      { kind: 'fleet', format: 'xlsx', ...reportParams }, 'fleet-trips.xlsx');
                  } finally { setBusy(null); }
                }}>
                <Download className="h-4 w-4" /> Fleet trips Excel
              </Button>
              <Button variant="outline" size="sm" className="gap-2" disabled={busy === 'p2'}
                onClick={async () => {
                  setBusy('p2');
                  try {
                    await download('/api/v1/vehicle-history/report',
                      { kind: 'fleet', format: 'pdf', ...reportParams }, 'fleet-trips.pdf');
                  } finally { setBusy(null); }
                }}>
                <Download className="h-4 w-4" /> Fleet trips PDF
              </Button>
              <Button variant="gold" size="sm" className="gap-2" disabled={!plate || busy === 'share'}
                onClick={() => makeShare('vehicle')}>
                <Share2 className="h-4 w-4" /> Share link
              </Button>
            </div>

            {shared && (
              <div className="mt-2 rounded-md border border-border bg-muted/40 p-2 text-xs break-all">
                Copied. Read-only, works without a login, expires in 7 days:
                <span className="font-mono ms-1">{shared}</span>
              </div>
            )}
            {status?.legCoverage?.lo && (
              <div className="mt-2 text-xs text-muted-foreground">
                Trips are rebuilt from GPS for {status.legCoverage.lo} to {status.legCoverage.hi}.
                Outside that window the map still draws, but legs are not computed.
              </div>
            )}
          </div>
        </div>

        {/* ---- stat strip ------------------------------------------------ */}
        {s && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            {[
              ['Legs', s.legs, ''],
              ['In records', s.inRecords, 'text-emerald-600'],
              ['Unreported', s.unreported, s.unreported ? 'text-rose-600' : 'text-muted-foreground'],
              ['Not billable', s.notBillable, 'text-muted-foreground'],
              ['Distance', `${s.km.toLocaleString('en-US')} km`, ''],
            ].map(([l, v, c], i) => (
              <div key={i} className="rounded-xl border border-border bg-card p-4 min-w-0">
                <div className="text-xs text-muted-foreground">{l as string}</div>
                <div className={`text-2xl font-semibold mt-1 ${c as string}`}>{v as string}</div>
              </div>
            ))}
          </div>
        )}

        {/* ---- map ------------------------------------------------------- */}
        <div className="rounded-xl border border-border bg-card overflow-hidden mb-4 min-w-0">
          <div ref={mapEl} className="h-[380px] sm:h-[460px] w-full bg-muted" />
          <div className="border-t border-border p-3 flex flex-wrap items-center gap-3">
            <Button size="sm" variant={playing ? 'outline' : 'default'} className="gap-2"
              disabled={!track?.points.length}
              onClick={() => {
                if (!track?.points.length) return;
                if (cursor >= track.points.length - 1) setCursor(0);
                setPlaying((p) => !p);
              }}>
              {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {playing ? 'Pause' : 'Play'}
            </Button>
            <input type="range" min={0} max={Math.max(0, (track?.points.length ?? 1) - 1)} value={cursor}
              onChange={(e) => { setPlaying(false); setCursor(Number(e.target.value)); }}
              className="flex-1 min-w-[160px] accent-primary" />
            <div className="text-sm tabular-nums text-muted-foreground min-w-[180px]">
              {at ? `${niceDay(at.t)} ${hhmm(at.t)} · ${Math.round(at.speed ?? 0)} kph` : 'no GPS in this range'}
            </div>
          </div>
          <div className="border-t border-border px-3 py-2 flex flex-wrap gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <i className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: '#10b981' }} /> Loading site
            </span>
            <span className="flex items-center gap-1.5">
              <i className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: '#ef4444' }} /> Unloading site
            </span>
            <span className="flex items-center gap-1.5">
              <i className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: '#f59e0b' }} /> Both
            </span>
            <span className="flex items-center gap-1.5">
              <i className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: '#94a3b8' }} /> Role not set
            </span>
            {track && track.totalPoints > track.points.length && (
              <span>Showing {track.points.length.toLocaleString('en-US')} of {track.totalPoints.toLocaleString('en-US')} fixes for smooth playback.</span>
            )}
          </div>
        </div>

        {isLoading && <div className="text-sm text-muted-foreground">Loading history…</div>}
        {isError && (
          <div className="rounded-xl border border-rose-500/40 bg-rose-500/5 p-4 text-sm text-rose-600">
            Could not load this range. {(error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? ''}
          </div>
        )}

        {/* ---- legs and stops -------------------------------------------- */}
        <div className="grid gap-4 xl:grid-cols-2 min-w-0">
          <div className="rounded-xl border border-border bg-card overflow-x-auto min-w-0">
            <div className="px-3 py-2 border-b border-border font-medium text-sm">
              Trips ({track?.legs.length ?? 0})
            </div>
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-start font-medium px-3 py-2">Date</th>
                  <th className="text-start font-medium px-3 py-2">Route</th>
                  <th className="text-end font-medium px-3 py-2">Km</th>
                  <th className="text-start font-medium px-3 py-2">Records</th>
                </tr>
              </thead>
              <tbody>
                {track?.legs.map((l, i) => (
                  <tr key={i} className={`border-t border-border ${l.state === 'unreported' ? 'bg-rose-500/5' : ''}`}>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {niceDay(l.departed_at)}
                      <span className="text-muted-foreground ms-1">{hhmm(l.departed_at)}</span>
                    </td>
                    <td className="px-3 py-2">
                      {l.from_terminal} → {l.to_terminal}
                      <div className="text-xs text-muted-foreground">
                        arrived {hhmm(l.arrived_at)} · {asDur(mins(l.departed_at, l.arrived_at))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">{l.km ?? 0}</td>
                    <td className="px-3 py-2">
                      {l.state === 'in_records' && (
                        <span className="text-emerald-600 inline-flex items-center gap-1">
                          <Check className="h-3.5 w-3.5" />
                          {l.rec_vendor ?? 'matched'}
                          {l.rec_item ? <span className="text-muted-foreground">· {l.rec_item}</span> : null}
                        </span>
                      )}
                      {l.state === 'unreported' && (
                        <span className="text-rose-600 font-semibold">UNREPORTED</span>
                      )}
                      {l.state === 'not_billable' && (
                        <span className="text-muted-foreground" title="The records have never billed this lane, so no row is due.">
                          not billable
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {track && !track.legs.length && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                    No completed leg between two sites in this range.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-border bg-card overflow-x-auto min-w-0">
            <div className="px-3 py-2 border-b border-border font-medium text-sm">
              Stops of 45 minutes or more ({track?.stops.length ?? 0})
            </div>
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="text-start font-medium px-3 py-2">#</th>
                  <th className="text-start font-medium px-3 py-2">Site</th>
                  <th className="text-start font-medium px-3 py-2">Arrived</th>
                  <th className="text-end font-medium px-3 py-2">Stood</th>
                </tr>
              </thead>
              <tbody>
                {track?.stops.map((st, i) => (
                  <tr key={i} className="border-t border-border hover:bg-muted/40 cursor-pointer"
                    onClick={() => mapRef.current?.setView([st.lat, st.lng], 13)}>
                    <td className="px-3 py-2">
                      <span className="inline-flex h-5 w-5 items-center justify-center rounded-full text-[11px] text-white"
                        style={{ background: roleColour(st.role) }}>{i + 1}</span>
                    </td>
                    <td className="px-3 py-2">
                      {st.terminal ?? <span className="text-muted-foreground inline-flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5" /> Unnamed stop</span>}
                      {st.role && <div className="text-xs text-muted-foreground">{st.role}</div>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {niceDay(st.started_at)} {hhmm(st.started_at)}
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">{asDur(st.minutes)}</td>
                  </tr>
                ))}
                {track && !track.stops.length && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">
                    No stop of 45 minutes or more in this range.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
